import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IconButton } from '@material-ui/core';
import { Backspace } from '@material-ui/icons';
import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner, Chip } from '~/components';
import { Select, Input } from '~/components/Form';
import { TextField } from '~/components/Form/v2';
import { CUSTOMIZACAO_RAIZEN } from '~/constants/modules';
import { can, t, validCharacters } from '~/helpers';
import {
  storeMotorista,
  updateMotorista,
} from '~/store/modules/motorista/actions';

import { ContainerClear } from './styles';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { leitortagmotorista } = useSelector((state) => state.user.configs);
  const { loading, find, params } = useSelector((state) => state.motorista);

  const initials = create ? data : { ...find, ...data };
  const [formid, setFormid] = useState(0);

  const optionsMultiple = useMemo(() => {
    const { formaidmotorista } = find;

    if (formaidmotorista === 1) {
      return [1];
    }

    if (formaidmotorista === 2) {
      return [2];
    }

    if (formaidmotorista === 3) {
      return [1, 2];
    }

    if (formaidmotorista === 4) {
      return [4];
    }

    if (formaidmotorista === 5) {
      return [1, 4];
    }

    if (formaidmotorista === 6) {
      return [2, 4];
    }

    if (formaidmotorista === 7) {
      return [1, 2, 4];
    }

    return [];
  }, [find]);

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        idmotorista: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(11, t('s.geral.validacao.tamanhomaximo').replace('%s', 11))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        nomemotorista: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(50, t('s.geral.validacao.tamanhomaximo').replace('%s', 50))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        codigoauxiliarmotorista: Yup.string()
          .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20))
          .nullable()
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        senhamotorista: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10)),

        statusmotorista: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(1, t('s.geral.validacao.tamanhomaximo').replace('%s', 1)),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      param.formaidmotorista = formid;

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,
      };

      if (create) {
        await dispatch(storeMotorista(objSend, params));
      } else {
        await dispatch(updateMotorista(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Input
            name="idmotorista"
            label={t('s.cadastro.motorista.idmotorista')}
            variant="outlined"
            fullWidth
            size="small"
            disabled={!create}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Input
            name="nomemotorista"
            label={t('s.cadastro.motorista.nomemotorista')}
            variant="outlined"
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            disabled={can(CUSTOMIZACAO_RAIZEN)}
          />

          <Input
            name="codigoauxiliarmotorista"
            label={t('s.configuracao.comboio.codigoauxiliarposto')}
            variant="outlined"
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Input
            name="senhamotorista"
            label={t('s.cadastro.motorista.senhamotorista')}
            variant="outlined"
            fullWidth
            type="password"
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Select
            name="statusmotorista"
            variant="outlined"
            label={t('s.cadastro.motorista.statusmotorista')}
            options={[
              { value: 'L', label: t('s.geral.opcoes.liberado') },
              { value: 'B', label: t('s.geral.opcoes.bloqueado') },
              { value: 'C', label: t('s.geral.opcoes.cancelado') },
            ]}
            disabled={can(CUSTOMIZACAO_RAIZEN)}
          />

          {leitortagmotorista === 1 && (
            <>
              <Chip
                title={t('s.cadastro.motorista.formaidmotorista')}
                checkeds={optionsMultiple}
                getValue={(value) =>
                  setFormid(value.reduce((a, b) => a + b, 0))
                }
              />

              <ContainerClear>
                <TextField
                  disabled
                  name="tagmotorista"
                  label={t('s.cadastro.motorista.tagmotorista')}
                />

                <IconButton
                  onClick={() =>
                    reference.current.setFieldValue('tagmotorista', '')
                  }
                  disabled={can(CUSTOMIZACAO_RAIZEN)}
                >
                  <Backspace />
                </IconButton>
              </ContainerClear>
            </>
          )}
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
